/**
 * The month under a lapse or an empty calendar: ONE schedule read for the selected card, and — for
 * the empty calendar only — the running plan's slots, so its un-booked days can stand as ghosts.
 *
 * The same gate as the week strip's (`useClientWeek`): a card that is not on the desk reads nothing,
 * and a client with no id (a pending invitation) has no schedule. The whole month waits for both of
 * its reads, because a month drawn before its ghosts arrive would grow three hatched squares under
 * the trainer's eyes.
 */

import { useQuery } from '@tanstack/react-query';

import { customerScheduleQueryOptions, scheduleWindow } from '~/entities/calendar-event';
import {
  activeAssignment,
  customerAssignmentsQueryOptions,
  executionSummaryQueryOptions,
  type AssignmentFacts,
} from '~/entities/plan-assignment';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import type { ClientWeekEvent } from '../model/clientWeek';
import { monthCalendar, monthSpanDays, type MonthCalendar, type MonthSpan } from '../model/monthCalendar';
import { unbookedPlanDays } from '../model/planExecution';

export interface ClientMonthState {
  /** `null` while a read is open, and after the schedule read failed. */
  readonly month: MonthCalendar | null;
  readonly pending: boolean;
  /** The schedule read failed — the month is missing, not empty. */
  readonly failed: boolean;
  /** The running plan, where the card asked for it and the client has one. */
  readonly plan: AssignmentFacts | null;
}

export function useClientMonth({
  customerId,
  todayIso,
  span,
  withPlan,
}: {
  readonly customerId: number | null;
  readonly todayIso: string;
  readonly span: MonthSpan;
  /** Read the running plan too — the empty calendar's ghosts and its "shift the rest" act. */
  readonly withPlan: boolean;
}): ClientMonthState {
  const contentLang = useContentLanguage();
  const days = monthSpanDays(todayIso, span);
  const enabled = customerId !== null;
  const schedule = useQuery(
    customerScheduleQueryOptions(customerId ?? 0, scheduleWindow(days.first, days.last), { enabled }),
  );
  const assignments = useQuery(
    customerAssignmentsQueryOptions(customerId ?? 0, { enabled: enabled && withPlan }),
  );
  const plan = withPlan ? activeAssignment(assignments.data) : null;
  const summary = useQuery(
    executionSummaryQueryOptions(plan?.id ?? 0, contentLang, { enabled: plan !== null }),
  );

  if (!enabled) return { month: null, pending: false, failed: false, plan: null };
  if (schedule.isError) return { month: null, pending: false, failed: true, plan };
  const planPending = withPlan && (assignments.isPending || (plan !== null && summary.isPending));
  if (schedule.data === undefined || planPending) return { month: null, pending: true, failed: false, plan };

  const events: readonly ClientWeekEvent[] = schedule.data.map((event) => ({
    displayDate: event.displayDate,
    eventDate: event.eventDate,
    eventInstant: event.eventInstant,
    moment: event.moment,
    status: event.status,
    derivedStatus: event.derivedStatus ?? null,
    sessionName: event.sessionName ?? null,
  }));
  const ghosts = summary.data === undefined ? [] : unbookedPlanDays(summary.data);

  return { month: monthCalendar(events, todayIso, span, ghosts), pending: false, failed: false, plan };
}
